import { Component, EventEmitter, Input, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { DeviceFactory } from '../../../../device-factory/device-factory';
import { ColumnsConfig, TableConfig } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { ActivatedRoute } from '@angular/router';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import {
	AH_RETURN_BANDS,
	getScoreBand,
	SCORE_BAND_COLORS,
	SCORE_RANGE_COLORS,
	SCORE_RANGE_COLOR_ORDER,
	SCORE_RANGE_LABEL_COLORS,
	ScoreBand,
	ScoreRangeColor,
	UTILIZATION_SCORE_BANDS
} from 'src/app/shared/sct-charts/score-range-colors';

interface ScoreRange {
	from: number;
	to: number;
	label: string;
	color: ScoreRangeColor;
	band?: ScoreBand;
	devices: string[];
}

// Google Charts moves a label outside a bar too short to hold it, where a white label would vanish
// against the background - so only bars at least this wide (% of total batteries) get a white label.
const MIN_PERCENT_FOR_LIGHT_LABEL = 10;

@Component({
	selector: 'app-percent-battery-utilization-scores',
	templateUrl: './percent-battery-utilization-scores.component.html',
	styleUrls: ['./percent-battery-utilization-scores.component.css']
})
export class PercentBatteryUtilizationScoresComponent implements OnDestroy {
	@ViewChild('popupModal') popupModal;
	@Input() devices: any = {};
	@Input() data: any = {};

	@Input() userParentFilters: boolean = false;
	@Output() prepareParentFiltersOptions = new EventEmitter<{ batteryVoltagesOptions: any[], batteryCapacitiesOptions: any[], manufacturers: any[] }>();

	siteID: number = 0;
	customerID: number = 0;

	modalTitle: string = '';
	scoreRangeTitle: string = 'utilization.percent_utilization_score';
	tableData = [];
	columnConfig: ColumnsConfig[] = [
		{ key: 'serial_number', name: this.translate.instant('g.serial_number'), type: "link"},
		{ key: 'device_id', name: this.translate.instant('battlinkDevice.devices.truck_id'), type: "string" },
		{ key: 'value', name: this.translate.instant(`${this.scoreRangeTitle}`), type: "number"},
	];

	tableConfig: TableConfig = {
		hasExport: true,
		fileName: this.translate.instant(this.scoreRangeTitle) || '',
		hasPagination: true,
		pageSize: 100,
		csvFileHeaderInfo: []
	};

	utilizationScoreSelectedData = [];
	energyScoreSelectedData = [];
	ahReturnSelectedData = [];

	rangesUtilization: ScoreRange[] = this.buildScoreRanges();
	rangesEnergy: ScoreRange[] = this.buildScoreRanges();
	rangesAh: ScoreRange[] = this.buildAhReturnRanges();

	utilizationLegend = this.buildUtilizationLegend();
	ahReturnLegend = this.buildAhReturnLegend();

	utilizationChartData = [];
	energyChartData = []
	ahChartData = [];
	noData = false;
	// Two stacked series that look the same: a bar is drawn by series 0 when its label is dark and by
	// series 1 when it is white, since Google Charts can only set the label color per series.
	columnNames = [
		'Score Range',
		'% of Total Batteries',
		{ role: 'style' },
		{ role: 'annotation' },
		{ role: 'tooltip', type: 'string', p: { html: true } },
		'% of Total Batteries',
		{ role: 'style' },
		{ role: 'annotation' },
		{ role: 'tooltip', type: 'string', p: { html: true } }
	];

	public barChartOptions = {
		legend: { position: 'none' },

		hAxis: {
			title: '% of Total Batteries',
			minValue: 0,
			maxValue: 100,
			format: '#\'%\''
		},
		tooltip: {
			isHtml: true
		},
		vAxis: {
			title: 'Score Range'
		},

		annotations: {
			alwaysOutside: false,
			highContrast: false,
			textStyle: {
			fontSize: 12,
			color: SCORE_RANGE_LABEL_COLORS.dark
			}
		},

		bar: { groupWidth: '70%' },
		isStacked: true,
		// no white outline ("aura") behind the labels - the design has none and it blurs white text
		series: {
			0: { annotations: { textStyle: { fontSize: 12, color: SCORE_RANGE_LABEL_COLORS.dark, auraColor: 'none' } } },
			1: { annotations: { textStyle: { fontSize: 12, color: SCORE_RANGE_LABEL_COLORS.light, auraColor: 'none' } } }
		},
		colors: ['#8bcbe5'],

		chartArea: {
			left: 100,
			right: 30,
			top: 40,
			bottom: 50
		}
	};

	batteryDeviceType = DeviceFactory.types.battlink;

	periods = [
		{ value: 'last_week', label: this.translate.instant('durations.last_week') },
		{ value: 'last_7_days', label: this.translate.instant('durations.last_7_days') },
		{ value: 'last_30_days', label: this.translate.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translate.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translate.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translate.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translate.instant('durations.all_years') },
	];

	@Input() selectedPeriod = this.periods[0].value;

	batteryVoltagesOptions: any[] = [];
	@Input() selectedBatteryVoltage: string = 'all';
	batteryCapacitiesOptions: any[] = [];
	@Input() selectedBatteryCapacity: string = 'all';

	@Input() selectedBatteryVintage: string[] = ['all'];

	manufacturersOptions: any[] = [];
	@Input() selectedManufacturer: string = 'all';

	constructor(
		private translate: TranslateService,
		private commonService: CommonService,
		private route: ActivatedRoute,
		private siteDashboardService: SiteDashboardService,
	) { }

	ngOnInit() {
		this.siteID = this.route.parent.snapshot.params.siteId || 0;
		this.customerID = this.route.parent.snapshot.params.customerId || 0;

		this.prepareFilters();
		this.updateSelectedData();
	}

	ngOnChanges(changes: SimpleChanges) {
		this.prepareFilters();
		this.updateSelectedData();

		if (this.userParentFilters) {
			if (changes?.['selectedPeriod'])
				this.onSelectionChanged({ filter: 'period', value: changes['selectedPeriod'].currentValue });

			if (changes?.['selectedBatteryVoltage'])
				this.onSelectionChanged({ filter: 'voltageFilter', value: changes['selectedBatteryVoltage'].currentValue });

			if (changes?.['selectedBatteryCapacity'])
				this.onSelectionChanged({ filter: 'capacityFilter', value: changes['selectedBatteryCapacity'].currentValue });

			if (changes?.['selectedBatteryVintage'])
				this.onSelectionChanged({ filter: 'vintageFilter', value: changes['selectedBatteryVintage'].currentValue });

			if (changes?.['selectedManufacturer'])
				this.onSelectionChanged({ filter: 'manufacturerFilter', value: changes['selectedManufacturer'].currentValue });
		}
	}

	prepareFilters() {
		const batteriesCapacities = [];
		const batteriesVoltages = [];
		const manufacturers = [];

		for (const device in this.devices) {
			const deviceData = this.devices[device];
			if (deviceData.battery_capacity && !batteriesCapacities.includes(deviceData.battery_capacity))
				batteriesCapacities.push(deviceData.battery_capacity);

			if (deviceData.battery_voltage && !batteriesVoltages.includes(deviceData.battery_voltage))
				batteriesVoltages.push(deviceData.battery_voltage);

			if (deviceData.truck_manufacturer && !manufacturers.includes(deviceData.truck_manufacturer))
				manufacturers.push(deviceData.truck_manufacturer);
		}

		this.batteryVoltagesOptions = this.commonService.prepareOptions(batteriesVoltages);
		this.batteryCapacitiesOptions = this.commonService.prepareOptions(batteriesCapacities);
		this.manufacturersOptions = this.commonService.prepareOptions(manufacturers);

		this.prepareParentFiltersOptions.emit({ batteryVoltagesOptions: this.batteryVoltagesOptions, batteryCapacitiesOptions: this.batteryCapacitiesOptions, manufacturers: this.manufacturersOptions });
	}

	onSelectionChanged(event) {
		switch (event.filter) {
			case 'period':
				this.selectedPeriod = event.value;
				break;
			case 'voltageFilter':
				this.selectedBatteryVoltage = event.value;
				break;
			case 'capacityFilter':
				this.selectedBatteryCapacity = event.value;
				break;
			case 'vintageFilter':
				this.selectedBatteryVintage = event.value;
				break;
			case 'manufacturerFilter':
				this.selectedManufacturer = event.value;
				break;
			default:
				break;
		}

		this.updateSelectedData();
	}

	updateSelectedData() {
		const selectedData = this.data?.[this.selectedPeriod]?.['allDays'];

		this.utilizationScoreSelectedData = [];
		this.energyScoreSelectedData = [];
		this.ahReturnSelectedData = [];

		const usageScoreMap = new Map(
			(selectedData?.devicesUsageScore || []).map(device => [device.macAddress, device])
		);

		const AhScoreMap = new Map(
			(selectedData?.batteryAhScoreList || []).map(device => [device.macAddress, device])
		);

		for (const macAddress in this.devices) {
			if (!this.isIncludedDevice(macAddress))
				continue;

			// devicesUsageScore / batteryAhScoreList are only populated for batteries installed
			// during the selected period (siteUtilizationReportDAO.js getSiteUtilizationScoreData) -
			// skip a device entirely when it's absent, instead of defaulting it to a 0% bar that
			// still counts toward the chart's percentage denominator.
			if (!usageScoreMap.has(macAddress))
				continue;

			const usageScore: {score?: number, energy_score?: number} = usageScoreMap.get(macAddress);
			const ahScore: {score?: number} = AhScoreMap.get(macAddress);

			this.utilizationScoreSelectedData.push({
				macAddress,
				score: usageScore?.score ?? 0
			});

			this.energyScoreSelectedData.push({
				macAddress,
				score: usageScore?.energy_score ?? 0
			});

			// Every battery is counted, as on the other two charts: one with no in-use Ah (idle, or charged
			// but never used) gets a 0% score from the DAO and lands in the < 85% band.
			this.ahReturnSelectedData.push({
				macAddress,
				score: Math.min(ahScore?.score ?? 0, 150),
			});
		}

		this.utilizationChartData = this.buildPercentChartData(this.utilizationScoreSelectedData, this.rangesUtilization);
		this.energyChartData = this.buildPercentChartData(this.energyScoreSelectedData, this.rangesEnergy);
		this.ahChartData = this.buildPercentChartData(this.ahReturnSelectedData, this.rangesAh);

		// Batteries that fail the shipping check never reach us, so an empty list means there is
		// nothing to score. buildPercentChartData still returns a row per range, every one at zero,
		// which is why the charts used to draw all-zero bars instead of an empty state.
		this.noData = !this.utilizationScoreSelectedData.length;
	}

	isIncludedDevice(macAddress: string) {
		if (this.selectedBatteryVoltage !== 'all' && this.devices?.[macAddress]?.battery_voltage !== this.selectedBatteryVoltage)
			return false;

		if (this.selectedBatteryCapacity !== 'all' && this.devices?.[macAddress]?.battery_capacity !== this.selectedBatteryCapacity)
			return false;

		if (this.selectedManufacturer !== 'all' && this.devices?.[macAddress]?.truck_manufacturer !== this.selectedManufacturer)
			return false;

		if (!this.siteDashboardService.isVintageWithinFilter(this.devices?.[macAddress], this.selectedBatteryVintage))
			return false;

		return true;
	}

	// Utilization / Energy ranges: ten 10% bars, the last one open-ended so scores above 100% (energy
	// usage can exceed it) land in "91% and above" instead of adding bars.
	buildScoreRanges(): ScoreRange[] {
		const ranges: ScoreRange[] = [];
		for (let i = 0; i < 100; i += 10) {
			const from = i ? i + 1 : 0;
			const to = i + 10 >= 100 ? Infinity : i + 10;

			ranges.push({
				from,
				to,
				label: this.formatRangeLabel(from, to),
				// Same table as the battery gauges, so a bar and a gauge never disagree (two 10% bars per color).
				// Only the color is taken: a band here would turn the bar popup title into "Bad (0–10%)".
				color: SCORE_BAND_COLORS[getScoreBand(UTILIZATION_SCORE_BANDS, from)],
				devices: []
			});
		}
		return ranges;
	}

	buildAhReturnRanges(): ScoreRange[] {
		return AH_RETURN_BANDS.map(({ from, to, band }) => ({
			from,
			to,
			label: from === 0
				? this.translate.instant('utilization.score_range_below', { value: to + 1 })
				: this.formatRangeLabel(from, to),
			color: SCORE_BAND_COLORS[band],
			band,
			devices: []
		}));
	}

	formatRangeLabel(from: number, to: number) {
		return to === Infinity ? this.translate.instant('utilization.score_range_and_above', { value: from }) : `${from}–${to}%`;
	}

	buildUtilizationLegend() {
		return SCORE_RANGE_COLOR_ORDER.map(color => {
			const ranges = this.rangesUtilization.filter(range => range.color === color);
			return {
				label: this.formatRangeLabel(ranges[0].from, ranges[ranges.length - 1].to),
				color: SCORE_RANGE_COLORS[color]
			};
		});
	}

	buildAhReturnLegend() {
		const bands: ScoreBand[] = ['optimal', 'good', 'marginal', 'poor', 'bad'];
		return bands.map(band => ({
			name: this.translate.instant(`battery_return_score.${band}`),
			ranges: this.rangesAh.filter(range => range.band === band).map(range => range.label).join(', '),
			color: SCORE_RANGE_COLORS[SCORE_BAND_COLORS[band]]
		}));
	}

	buildPercentChartData(data: { macAddress: string, score: number }[], ranges: ScoreRange[]) {
		const total = data.length || 1;
		ranges.forEach(r => r.devices = []);
		for (const d of data) {
			const value = Math.floor(d.score || 0);
			const range = ranges.find(range => value >= range.from && value <= range.to);
			if (range)
				range.devices.push(d.macAddress);
		}

		return ranges.map(r => {
			const count = r.devices.length;
			const percent = total ? Math.round((count / total) * 100) : 0;
			const tooltip = `
				<div style="padding:8px; white-space: nowrap;">
					<div><strong>Count of Batteries: ${count}</strong></div>
					<div><strong>Total Count of Batteries: ${total}</strong> </div>
				</div>
			`;

			const style = `color: ${SCORE_RANGE_COLORS[r.color]}`;
			// ST-2764: the number inside a bar is white on every color.
			const isLightLabel = percent >= MIN_PERCENT_FOR_LIGHT_LABEL;

			// The series not drawing this bar gets 0 and an empty label rather than null: sct-charts turns
			// null into 0, and a "0" label would then be drawn next to the bar.
			return [
				r.label,
				isLightLabel ? 0 : percent, style, isLightLabel ? '' : `${percent}`, tooltip,
				isLightLabel ? percent : 0, style, isLightLabel ? `${percent}` : '', tooltip
			];
		});
	}

	onBarSelect(event, ranges, sourceData, title) {
		if (!event || event.row === undefined) 
			return;

		const selectedRange = ranges[event.row];
		if (!selectedRange || !selectedRange.devices.length) 
			return;

		this.tableData = [];

		for (const macAddress of selectedRange.devices) {
			const device = this.devices[macAddress];
			const score = sourceData.find(d => d.macAddress === macAddress)?.score || 0;

			this.tableData.push({
				serial_number: { value: device.serial_number, link: ['/', this.customerID, this.siteID, device.mac_address, 'performance'] },
				device_id: { value: device.truck_id },
				value: { value: score }
			});
		}

		// Ah Return bands repeat (Good is both 100–104% and 116–120%), so the title names the band and its range
		const bandName = selectedRange.band ? this.translate.instant('battery_return_score.' + selectedRange.band) : '';
		this.modalTitle = bandName ? `${bandName} (${selectedRange.label})` : selectedRange.label;
		this.scoreRangeTitle = title;
		this.columnConfig[2].name = this.translate.instant(`${this.scoreRangeTitle}`);
		this.tableConfig.fileName = this.translate.instant(this.scoreRangeTitle) || '';
		this.popupModal.show();
	}

	ngOnDestroy() {
		this.popupModal.hide();
	}
}
